<template>
    <ul
        tabindex="-1"
        @focus="onFocus"
        role="menu"
        :aria-labelledby="ariaLabelledby"
        :aria-label="ariaLabelledby ? null : 'Selectable list'"
        :aria-activedescendant="highlightedId"
    >
        <li
            v-for="(item, index) in items"
            ref="item"
            class="list-item"
            :key="index"
            :class="{
                selected: selected[index],
                highlighted: index === highlightedIndex,
                disabled: item.disabled,
                nowrap,
            }"
            :style="{ padding, ...(item.style || {}) }"
            @mouseover="changeHighlight(index)"
            @mouseleave="changeHighlight(-1)"
            @click="select(index)"
            :id="itemIds[index]"
            role="menuitem"
        >
            <i v-if="multiple && !item.single" :class="`far fa-${selected[index] ? 'check-' : ''}square`"></i>
            <i v-if="item.icon" :class="`far fa-${item.icon}`"></i>
            <span v-html="item[labelPropName] || '&nbsp;'"></span>
        </li>
    </ul>
</template>

<script>
    import { dom, constants, strings } from './../utils';

    export default {
        name: 'SelectableList',
        data() {
            return {
                highlightedIndex: -1,
            };
        },
        props: {
            items: {
                type: Array,
                required: true,
            },
            onSelect: {
                type: Function,
            },
            active: {
                // up/down/select will work only if active
                type: Boolean,
                required: true,
            },
            value: {
                type: [Number, String, Array],
                default: '',
            },
            labelPropName: {
                type: String,
                default: 'name',
            },
            valuePropName: {
                type: String,
                default: 'value',
            },
            ariaLabelledby: {
                type: String,
            },
            padding: {
                type: String,
                default: '',
            },
            multiple: {
                type: Boolean,
                default: false,
            },
            allowNone: {
                type: Boolean,
                default: true,
            },
            nowrap: {
                type: Boolean,
                default: false,
            },
        },
        computed: {
            itemIds() {
                return this.items.map(() => `list_item_${strings.random()}`);
            },
            highlightedId() {
                return this.itemIds[this.highlightedIndex] || null;
            },
            selected() {
                return this.items.map((item) => {
                    if (this.multiple && Array.isArray(this.value)) {
                        return this.value.includes(item[this.valuePropName]);
                    }
                    return item[this.valuePropName] === this.value;
                });
            },
            firstSelectedIndex() {
                return this.selected.indexOf(true);
            },
        },
        methods: {
            keydown(e) {
                if (!this.active) return;

                if (e.which === constants.KEY_UP) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.up();
                } else if (e.which === constants.KEY_DOWN) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.down();
                } else if (e.which === constants.KEY_ENTER) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.selectHighlighted();
                } else if (e.which === constants.KEY_HOME) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.highlightedIndex = 0;
                } else if (e.which === constants.KEY_END) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.highlightedIndex = this.items.length - 1;
                }
            },
            up() {
                if (this.highlightedIndex > 0) this.highlightedIndex--;
            },
            down() {
                if (this.highlightedIndex < this.items.length - 1) this.highlightedIndex++;
            },
            changeHighlight(index) {
                this.highlightedIndex = index;
            },
            selectHighlighted() {
                if (this.highlightedIndex !== -1) {
                    let elem = this.$refs.item[this.highlightedIndex];
                    if (!elem) return;
                    if (dom.isElementVisible(elem)) elem.click();
                }
            },
            select(index) {
                let item = this.items[index];
                if (item.disabled) return;

                this.highlightedIndex = index;
                if (typeof item.callback === 'function') {
                    item.callback();
                }

                if (typeof this.onSelect === 'function') {
                    if (this.multiple) {
                        let newValue;
                        if (item.single || !Array.isArray(this.value)) {
                            newValue = [item[this.valuePropName]];
                        } else {
                            newValue = this.value.slice();
                            if (this.selected[index]) {
                                if (this.allowNone || newValue.length > 1) {
                                    let i = newValue.indexOf(item[this.valuePropName]);
                                    newValue.splice(i, 1);
                                }
                            } else {
                                let i = 0;
                                for (let j = 0; j < index; j++) {
                                    if (this.selected[j]) i++;
                                }
                                newValue.splice(i, 0, item[this.valuePropName]);
                            }
                            for (let j = 0; j < this.items.length; j++) {
                                if (this.selected[j] && this.items[j].single) {
                                    let i = newValue.indexOf(this.items[j][this.valuePropName]);
                                    newValue.splice(i, 1);
                                }
                            }
                        }
                        this.onSelect(newValue);
                    } else {
                        this.onSelect(item);
                    }
                }
            },
            onFocus() {
                if (this.firstSelectedIndex >= 0) {
                    this.highlightedIndex = this.firstSelectedIndex;
                } else {
                    this.highlightedIndex = this.items.findIndex((item) => !item.disabled);
                }
            },
        },
        mounted() {
            document.addEventListener('keydown', this.keydown);
            this.highlightedIndex = this.firstSelectedIndex;
        },
        beforeDestroy() {
            document.removeEventListener('keydown', this.keydown);
        },
    };
</script>
<style lang="scss" scoped>
    ul {
        padding: 0;
        margin: 0;

        &:focus {
            outline: none;
        }
    }

    li {
        margin: 0;
        list-style: none;
        padding: 0.5em 0.75em;
        background: $color-white;
        font-size: $font-size-smaller;
        text-align: left;
        cursor: pointer;

        &.nowrap {
            white-space: nowrap;
        }

        &.selected {
            background: darken($color-disabled-bg, 4);
        }

        &:hover,
        &.highlighted {
            background: $color-disabled-bg;

            &.selected {
                background: darken($color-disabled-bg, 4);
            }
        }

        &.disabled {
            cursor: not-allowed;
            background: initial !important;
            color: $color-disabled-text !important;
        }

        ::v-deep i {
            width: 14px;
            text-align: center;
            margin-right: 0.4em;
            font-size: 14px;
            position: relative;
            top: 1px;
        }
    }
</style>